/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022-2025 (original work) Open Assessment Technologies SA;
 */

import env from './env.js';
import routes from './routes.js';

let tenants = [];

try {
    tenants = JSON.parse(env('TENANTS'));
    /* eslint-disable no-empty */
} catch (e) {}

export default {
    tenants,
    baseUrls: {
        api: env('API_URL'),
        authServer: env('AUTH_SERVER_URL') || 'https://auth-server.ngs.test',
        deliver: env('DELIVER_URL') || 'https://deliver.ngs.test',
        diagnostic: env('DIAGNOSTIC_URL') || 'https://diagnostic.ngs.test',
        proctoring: env('PROCTORING_URL') || 'https://pr-lti-gateway.ngs.test',
        grading: env('GRADING_URL') || 'http://ms-be.ngs.test',
        simpleReportsApi: env('SIMPLE_REPORTS_API_URL') || 'https://sr-be.ngs.test',
        aiWebSocket: env('WEBSOCKET_AI_PUBLIC_URL') || 'https://tao-ai-ws.ngs.test',
        aiApi: env('AI_API_URL') || 'https://tao-ai-api.ngs.test',
        deliveryPreview: env('DELIVERY_PREVIEW_URL') || 'https://test-runner-sandbox.ngs.test',
        studio: env('STUDIO_URL') || 'https://studio.ngs.test',
        portal: env('STATIC_URL'),
        payment: env('PAYMENT_URL') || 'https://payments-be.ngs.test',
        jsreport: env('JSREPORT_URL') || 'http://localhost:5488',
        wsTaskOrchestratorURL: env('WS_TASK_ORCHESTRATOR_URL')
    },
    routes,
    // Please follow A-Z order
    endpoints: {
        // A
        activityLog: '/api/v1/activity-log',
        // B
        batteries: '/api/v1/batteries',
        battery: '/api/v1/batteries/:id',
        // C
        campaign: '/api/v1/campaigns/:id',
        campaigns: '/api/v1/campaigns',
        changeCertificationSessionUserStatus: '/api/v1/sessions/certification/:sessionId/:userLogin',
        certificationScheduledSessions: '/api/v1/sessions/scheduled',
        certificationFinishedSessions: '/api/v1/sessions/finished',
        certificationScheduledSessionsByUser: '/api/v1/sessions/:login/scheduled',
        certificationFinishedSessionsByUser: '/api/v1/sessions/:login/finished',
        certificationSessionsAppealResult: '/api/v1/sessions/:userSessionId/appeal',
        converterCsvRequest: '/api/v1/ai/converter/csv',
        converterDocRequest: '/api/v1/ai/converter/msWord',
        converterInfo: '/api/v1/ai/converter/info',
        countries: '/api/v1/configuration/:tenantId/countries',
        // D
        delivery: '/api/v1/deliveries/:deliveryId',
        documentAIDelivery: '/api/v1/ai/document/delivery',
        downloadCsvTemplates: '/api/v1/import-data/download-csv-templates',
        diplomaExistsForCurrentUser: '/api/v1/diplomas/exists-current-user',
        diplomaGenerateForCurrentUser: '/api/v1/diplomas/generate-file-current-user',
        diplomaExistsForUser: '/api/v1/diplomas/:login/exists',
        diplomaGenerateForUser: '/api/v1/diplomas/:login/generate-file',
        // E
        editDelivery: '/test-editor/',
        emailVerification: '/api/v1/emails/verify',
        emailSubscriptionsCancel: '/api/v1/emails/subscriptions/cancel',
        emailVerificationSend: '/api/v1/emails/:login/verification/send',
        entitiesList: '/api/v1/data/entity/list',
        entity: '/api/v1/data/entity/:entityId/detail',
        entityData: '/api/v1/data/entity/:entityId/data',
        entityCsvData: '/api/v1/data/entity/:entityId/csvData',
        entityTableData: '/api/v1/data/entity/:entityId/table',
        exportCredentials: '/api/v1/sessions/:sessionId/credentials',
        exportEnrolments: '/api/v1/sessions/enrolments',
        exportResults: '/api/v1/sessions/:sessionId/results',
        exportAllSessionData: '/api/v1/sessions/:sessionId/export-all-session-data',
        exportPdfSignatureSheet: '/api/v1/sessions/:sessionId/export-signature-sheet/pdf',
        // G
        generateAIDelivery: '/api/v1/ai/generate',
        generatePreviewDeliveryLink: '/api/v1/lti/preview-delivery-link/:deliveryId',
        generateAIScoreSuggestion: '/api/v1/ai/generate/score/suggestion',
        getDocumentAIDelivery: '/api/v1/ai/document/delivery',
        getOrganizationTree: '/api/v1/hierarchies',
        group: '/api/v1/groups/:groupId',
        groups: '/api/v1/groups',
        'groups.users': '/api/v1/groups/:groupId/users',
        // H
        hierarchy: '/api/v1/hierarchies/:id',
        hierarchyActivate: '/api/v1/hierarchy-configs/activate',
        hierarchyAddChild: '/api/v1/hierarchies/add-child',
        hierarchyMigrateEntities: '/api/v1/hierarchies/migrate',
        // I
        importData: '/api/v1/import-data/csv',
        importDataAsync: '/api/v1/import-data/async-csv',
        infoAIDelivery: '/api/v1/ai/info',
        initiatePayment: '/api/v1/payments/:paymentId',
        // L
        launchAuthoring: '/api/v1/lti/launch-authoring',
        launchDeliveryExecution: '/api/v1/lti/launch-delivery-execution/:sessionId',
        launchPublicDeliveryExecution: '/api/v1/lti/launch-public-delivery-execution/:sessionId',
        launchGrader: '/api/v1/lti/launch-grader/',
        launchProctoringMonitoring: '/api/v1/lti/launch-proctoring-monitoring/:sessionId',
        launchUserProctoringMonitoring: '/api/v1/lti/launch-proctoring-monitoring/users/:user',
        login: '/v1/oauth2/tokens', // Auth Server's endpoint
        lookup: '/api/v1/data/lookup/:entityId',
        // M
        metadataSchema: '/api/v1/metadata/schema/:tenantId/:entity', // Classification service's endpoint
        metadataValidate: '/api/v1/metadata/:entity/validate', // Classification service's endpoint
        mfaRequestCode: '/v1/auth/mfa/request', // Auth Server's MFA endpoint
        mfaVerifyCode: '/v1/auth/mfa/verify', // Auth Server's MFA endpoint
        // N
        notificationsOptions: '/api/v1/notifications-options',
        // P
        paymentCredentials: 'api/v1/merchant-credentials/:organizationId',
        previewAIDelivery: '/',
        previewBattery: '/api/v1/lti/preview-battery/:batteryId',
        previewDeliveryExecution: '/api/v1/lti/preview-delivery-execution/:deliveryId',
        publishAIDelivery: '/api/v1/ai/publish',
        publishClonedDelivery: '/api/v1/deliveries/:id/publish-clone',
        publishDelivery: '/api/v1/publication/publish',
        publishTest: '/api/v1/publication/publish-test/:testId',
        publicSessions: '/api/v1/sessions/public/:subdomain',
        // R
        refreshToken: '/v1/oauth2/tokens', // Auth Server's endpoint,
        renderReport: '/api/report',
        reportsTranslations: '/api/v1/configuration/:tenantId/reports-translations/:locale',
        reviewDeliveryExecution: '/api/v1/lti/review-delivery-execution/:deliveryExecutionId/:itemId?',
        roles: '/api/v1/tenants/:tenantId/roles',
        // S
        searchAssets: '/api/v1/assets/search',
        downloadAsset: '/api/v1/assets/download/:virtualPath',
        searchBatteries: '/api/v1/batteries/search',
        searchCampaigns: '/api/v1/campaigns/search',
        searchCertificationSessionEnrolledUsers: '/api/v1/sessions/certification/:sessionId/users/search',
        searchDeliveries: '/api/v1/deliveries/search',
        searchGroups: '/api/v1/groups/search',
        searchSessions: '/api/v1/sessions/search',
        searchSessionUsers: '/api/v1/sessions/:sessionId/users/search',
        searchSessionTemplates: '/api/v1/session-templates/search',
        searchTags: '/api/v1/tags/search',
        searchUsers: '/api/v1/users/search',
        searchView: '/api/v1/data/views/:viewId',
        searchViews: '/api/v1/data/views',
        suggestedAIScore: '/api/v1/ai/score/suggestion/info',
        searchEnrollmentCandidates: '/api/v1/users/search-enrollment-users',
        enrollCandidates: '/api/v1/sessions/certification/:sessionId/users',
        moveCertificationSessionCandidates: '/api/v1/sessions/certification/move-users/:fromSessionId/:toSessionId',
        session: '/api/v1/sessions/:sessionId',
        sessionUser: '/api/v1/sessions/:sessionId/users/:userSessionId',
        sessionExtraTimes: '/api/v1/sessions/:sessionId/extra-times',
        sessionGradingStatus: '/api/v1/sessions/:sessionId/grading-status',
        sessions: '/api/v1/sessions',
        sessionLogin: '/v1/oauth2/session-tokens',
        sessionTemplates: '/api/v1/session-templates',
        sessionTemplate: '/api/v1/session-templates/:sessionTemplateId',
        'simpleReports.getReport': '/api/v1/report/slug/:slug',
        'simpleReports.getReports': '/api/v1/report',
        'simpleReports.getWidgetData': '/api/v1/widget/data/:id',
        ssoLogin: '/v1/sso/login/:clientId/:providerId', // Auth Server's endpoint
        studentSessions: '/api/v1/sessions/user',
        systemSettings: '/api/v1/system-settings',
        subdomain: '/v1/subdomains/:type/:subdomain', // Auth Server's endpoint
        supportUserSessions: '/api/v1/support/:user/data/sessions',
        supportUser: '/api/v1/support/:user/data/users',
        supportUserDeliveryExecutions: '/api/v1/support/:user/data/delivery-executions',
        supportUserResults: '/api/v1/support/:user/data/results',
        supportUserActivityLogs: '/api/v1/support/:user/data/activity-logs',
        supportUserProctoringLogs: '/api/v1/support/:user/data/proctoring-logs',
        supportUserPassword: '/api/v1/support/:user/data/password',
        supportEntityTable: '/api/v1/support/table/:entity',
        // T
        tags: '/api/v1/tags',
        tenantConfiguration: '/api/v1/configuration/:subdomain',
        testCategories: '/api/v1/configuration/:tenantId/test-categories',
        tests: '/api/v1/tests',
        // U
        updateAIDelivery: '/api/v1/ai/update',
        uploadAsset: '/api/v1/assets/upload',
        user: '/api/v1/users/:login',
        users: '/api/v1/users',
        userCurrent: '/api/v1/users/current-user',
        userCreateIntegration: '/api/v1/users/integration',
        userGroupRoles: '/api/v1/users/user/roles',
        'user.groups': '/api/v1/users/:login/groups',
        userIntegrationConfiguration: '/api/v1/users/integration/configuration',
        // V
        view: '/api/v1/data/view',
        viewId: '/api/v1/data/view/:viewId',
        // W
        waitingAIDelivery: '/website/waiting'
    }
};
